import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import useFavouritesStore from "@stores/shared/useFavouritesStore";
import useFavouritesUserCollectionStore from "@stores/client/useFavouritesUserCollectionStore";

export const useFavouriteManager = (
  open,
  setIsFavourited,
  onClose,
  favouriteId = null,
) => {
  const location = useLocation();
  const currentPathname = location.pathname;
  const { t } = useTranslation();

  const { favouritesUserCollections, fetchItems: fetchUserCollections } =
    useFavouritesUserCollectionStore();
  const { deleteFavourite, updateFavourite } = useFavouritesStore();

  const [favouriteName, setFavouriteName] = useState("");
  const [selectedCollection, setSelectedCollection] = useState("");
  const [existingFavourite, setExistingFavourite] = useState(null);

  useEffect(() => {
    if (open) {
      if (favouriteId) {
        setExistingFavourite({ id: favouriteId, url: currentPathname });
        setFavouriteName(currentPathname || "Untitled Page");
        if (favouritesUserCollections.length > 0) {
          setSelectedCollection(favouritesUserCollections[0].id);
        }
      } else {
        setExistingFavourite(null);
        setFavouriteName(currentPathname || "Untitled Page");
        if (favouritesUserCollections.length > 0) {
          setSelectedCollection(favouritesUserCollections[0].id);
        }
      }
    }
  }, [open, currentPathname, favouritesUserCollections, favouriteId]);

  const handleUpdate = () => {
    if (!favouriteName.trim()) {
      toast.error(t("favourites.enterFavouriteName"));
      return;
    }

    if (!selectedCollection) {
      toast.error(t("favourites.selectCollection"));
      return;
    }

    if (!existingFavourite) {
      onClose();
      return;
    }

    const hasNameChanged = existingFavourite.name !== favouriteName;
    const hasCollectionChanged =
      existingFavourite.favouriteCollectionID !== selectedCollection;

    if (hasNameChanged || hasCollectionChanged) {
      const updatedFavourite = {
        id: existingFavourite.id,
        name: favouriteName,
        type: existingFavourite.type,
        url: existingFavourite.url ?? currentPathname,
        order: existingFavourite.order,
        favouriteCollectionID: selectedCollection,
      };

      updateFavourite(
        existingFavourite.id,
        updatedFavourite,
        () => {
          toast.success(t("favourites.favouriteUpdateSuccess"));
          setIsFavourited(true);
          onClose();
          setFavouriteName("");
          setSelectedCollection("");
        },
        (error) => {
          toast.error(t("favourites.favouriteUpdateError"));
          console.error("Error updating favourite:", error);
        },
      );
    } else {
      onClose();
    }
  };

  const handleDelete = () => {
    if (existingFavourite) {
      deleteFavourite(
        existingFavourite.id,
        selectedCollection || existingFavourite.favouriteCollectionID,
        () => {
          toast.success(t("favourites.favouriteRemoveSuccess"));
          setIsFavourited(false);
          onClose();
          setExistingFavourite(null);
        },
        (error) => {
          toast.error(t("favourites.favouriteRemoveError"));
          console.error("Error removing favourite:", error);
        },
      );
    } else {
      onClose();
      setIsFavourited(false);
    }
  };

  return {
    favouriteName,
    setFavouriteName,
    selectedCollection,
    setSelectedCollection,
    existingFavourite,
    favouritesUserCollections,
    handleUpdate,
    handleDelete,
    fetchUserCollections,
  };
};
